/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from "react"
import * as S from "./styled"
import Header from "../../components/Header"
import Footer from "../../components/Footer"
import Container from "../../components/Container"
import { CreditCard, Check, Lock } from "lucide-react"

import pixImage from "../../assets/icons/payment/ic-pix.png"
import cardVisa from "../../assets/icons/payment/ic-visa.png"
import cardMaster from "../../assets/icons/payment/ic-master.png"
import cardAmex from "../../assets/icons/payment/ic-amex.png"
import cardDiners from "../../assets/icons/payment/ic-diners.png"
import cardElo from "../../assets/icons/payment/ic-elo.png"
import cardDiscover from "../../assets/icons/payment/ic-discover.png"
import OrderResume from "../../components/OrderResume"
import { TTicketDisposal } from "../../utils/@types/data/ticket"
import { TForm, TTicketForm, initialForm } from "../../utils/placeData/form"
import { useLocation, useNavigate } from "react-router-dom"
import { formatPhone } from "../../utils/masks/phone"
import { getDatePeriod } from "../../utils/tb/getDatePeriod"
import getStore from "../../store"
import { formatCardDate } from "../../utils/masks/date"
import { formatCardCode } from "../../utils/masks/cardcode"
import { formatCpf } from "../../utils/masks/cpf"
import { Api, checkTokenExpiration } from "../../api"
import {
  getAdditionalTax,
  getTicketRelativeTaxes,
  sumTaxes,
} from "../../utils/tb/taxes"
import { validEmail } from "../../utils/tb/validEmail"
import ConfirmPhoneNumberModal from "../../components/Modal/ConfirmPhoneNumber"
import Feedback from "../../components/Feedback"
import { TUser } from "../../utils/@types/data/user"
import {
  needsGuestCheckout,
  startGuestCheckout,
} from "../../utils/tb/guestCheckout"
import { TEventData } from "../../utils/@types/data/event"
import { isPhoneNumberValid } from "../../utils/tb/phone/isValidPhone"
import { getMpInstance } from "../../utils/mp/sdk"
import { isValidCpf } from "../../utils/tb/validCPF"
import { isValidCVV } from "../../utils/tb/validCVV"
import pageTools from "../../utils/tb/pageTools/pix"
import { useCardInstallments } from "../../hooks/useCardInstallments"
import InstallmentSelect from "../../components/InstallmentSelect"
import CouponField from "../../components/CouponField"
import { TCouponQuote } from "../../utils/@types/data/coupon"
import {
  COUPON_ZERO_TOTAL_MESSAGE,
  getCartKey,
  getCouponCharge,
  getCouponErrorMessage,
  getCouponProducts,
} from "../../utils/tb/coupon"

type IProps = {
  label: string
  value: string
  onChange: (v: string) => void
  onBlur?: () => void
  inputMode?: string
  enterKeyHint?: string
  helper?: string
  error?: string | boolean
  noNumbers?: boolean
  type?: string
  capitalizeWords?: boolean
}

const Input = ({
  label,
  value,
  onChange,
  onBlur,
  inputMode,
  enterKeyHint,
  helper,
  error,
  noNumbers = false,
  type = "text",
  capitalizeWords = false,
}: IProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value
    if (noNumbers) {
      newValue = newValue.replace(/[0-9]/g, "")
    }
    if (capitalizeWords) {
      newValue = newValue
        .toLowerCase()
        .split(" ")
        .map((word) => {
          if (word.length > 0) {
            return word.charAt(0).toUpperCase() + word.slice(1)
          }
          return word
        })
        .join(" ")
    }
    onChange(newValue)
  }

  const errorMsg = typeof error === "string" && error.length > 0 ? error : undefined
  const hasErrorState = !!error

  return (
    <S.InputWrapper>
      <S.Label>
        <S.Input
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={""}
          inputMode={inputMode as any}
          enterKeyHint={enterKeyHint as any}
          $error={hasErrorState}
        />
        <span>{label}</span>
      </S.Label>
      {errorMsg ? (
        <span className="inputError" role="alert" aria-live="polite">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle
              cx="8"
              cy="8"
              r="7"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
            />
            <path
              d="M8 6V8M8 10H8.01"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          {errorMsg}
        </span>
      ) : helper ? (
        <span className="inputHelper">{helper}</span>
      ) : null}
    </S.InputWrapper>
  )
}

type TCardFlag =
  | null
  | "VISA"
  | "MASTERCARD"
  | "AMERICAN EXPRESS"
  | "DISCOVER"
  | "DINERSCLUB"
  | "ELO"

const Payment = () => {
  const lctn = useLocation()
  const navigate = useNavigate()

  const store = getStore()
  const { event, controllers } = store

  const user = sessionStorage.getItem("user")
    ? JSON.parse(sessionStorage.getItem("user") as string)
    : null

  const pixEnabled = event?.eCommerce?.pix !== false
  const creditEnabled = event?.eCommerce?.credit !== false


  const [method, setMethod] = useState<"" | "pix" | "credit">(() => {
    const preselect = lctn.state?.preselectMethod as "pix" | "credit" | undefined
    if (preselect) return preselect
    if (event?.eCommerce?.pix !== false) return "pix"
    if (event?.eCommerce?.credit !== false) return "credit"
    return ""
  })
  const [form, setForm] = useState<TForm>({
    ...initialForm,
    buyer: {
      email: lctn.state?.buyer?.email ?? user?.email ?? "",
      name: lctn.state?.buyer?.name ?? user?.name ?? "",
      phone:
        lctn.state?.buyer?.phone ?? (user?.fone ? formatPhone(user?.fone) : ""),
    },
  })
  const [flag, setFlag] = useState<TCardFlag>(null)
  const [installments, setInstallments] = useState<number | null>(null)
  const [fieldsOk, setFieldsOk] = useState(false)
  const [canBuy, setCanBuy] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  // O ingresso vai para este número (WhatsApp): confirmar antes de pagar.
  const [showingPhoneModal, setShowingPhoneModal] = useState(false)
  const [payAfterPhoneConfirm, setPayAfterPhoneConfirm] = useState(false)

  const [tickets, setTickets] = useState<TTicketDisposal[]>([])

  // Cupom: vem de volta no state quando o comprador retorna do pagamento.
  const [coupon, setCoupon] = useState<TCouponQuote | null>(
    lctn.state?.coupon ?? null,
  )
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState<string>(
    lctn.state?.couponError ?? "",
  )
  const [feedback, setFeedback] = useState<any>({ visible: false, message: "" })
  const [formErrors, setFormErrors] = useState<{
    buyerName: boolean
    buyerPhone: boolean
    buyerEmail: boolean
    ticketsIds: number[]
  }>({
    buyerName: false,
    buyerPhone: false,
    buyerEmail: false,
    ticketsIds: [],
  })
  const [touched, setTouched] = useState<{
    buyerName: boolean
    buyerPhone: boolean
    buyerEmail: boolean
    cardCpf: boolean
    cardCvv: boolean
  }>({
    buyerName: false,
    buyerPhone: false,
    buyerEmail: false,
    cardCpf: false,
    cardCvv: false,
  })

  const markTouched = (field: keyof typeof touched) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }))
  }

  const handleForm = (field: keyof TForm["buyer"], value: string) => {
    if (!user && canBuy) setCanBuy(false)

    if (field === "phone") value = formatPhone(value)

    setForm({
      ...form,
      buyer: {
        ...form.buyer,
        [field]: value,
      },
    })
  }

  const usePayer = (ticket: TTicketForm, should: boolean) => {
    if (should) {
      setForm({
        ...form,
        tickets: form.tickets.map((t) =>
          // @ts-ignore
          t.id === ticket.id && t.oid === ticket.oid
            ? {
                ...t,
                person: {
                  name: form.buyer.name,
                  phone: form.buyer.phone,
                },
              }
            : t,
        ),
      })
    }
  }

  const handleTicketForm = (
    ticket: TTicketForm,
    field: keyof TTicketForm["person"],
    value: string,
  ) => {
    if (!user && canBuy) setCanBuy(false)

    setForm({
      ...form,
      tickets: form.tickets.map((t: any) =>
        t.id === ticket.id && t.oid === (ticket as any).oid
          ? {
              ...t,
              person: {
                ...t.person,
                [field]: value,
              },
            }
          : t,
      ),
    })
  }

  const checkFlag = (cNumber: string): TCardFlag => {
    const cardNumber = cNumber.replace(/\D/g, "")

    var visaRegEx = /^4[0-9]{12}(?:[0-9]{3})?$/
    var mastercardRegEx = /^5[1-5][0-9]{14}$/
    var americanExpressRegEx = /^3[47][0-9]{13}$/
    var discoverRegEx = /^6(?:011|5[0-9]{2})[0-9]{12}$/
    var dinersClubRegEx = /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/
    var eloRegEx =
      /^(40117[89]|4(?:011|44[0-9]{2}|5[0-9]{2}|[89][0-9]{2})\d{10}|627780|63\d{10}|6504\d{12}|6507\d{12}|6550\d{12})$/

    if (visaRegEx.test(cardNumber)) {
      return "VISA"
    } else if (mastercardRegEx.test(cardNumber)) {
      return "MASTERCARD"
    } else if (americanExpressRegEx.test(cardNumber)) {
      return "AMERICAN EXPRESS"
    } else if (discoverRegEx.test(cardNumber)) {
      return "DISCOVER"
    } else if (dinersClubRegEx.test(cardNumber)) {
      return "DINERSCLUB"
    } else if (eloRegEx.test(cardNumber)) {
      return "ELO"
    } else {
      return null
    }
  }

  const handleCard = (field: keyof TForm["card"], value: string) => {
    let v = value

    if (field === "number") {
      v = v
        .replace(/\D/g, "")
        .replace(/(\d{4})(?=\d)/g, "$1 ")
        .slice(0, 19)

      var checkedFlag = checkFlag(v)

      if (!!checkedFlag) setFlag(checkedFlag)
      else setFlag(null)
    }

    if (field === "date") v = formatCardDate(v)
    if (field === "code") v = formatCardCode(v)
    if (field === "cpf") v = formatCpf(v)

    setForm({
      ...form,
      card: {
        ...form.card,
        [field]: v,
      },
    })
  }

  const renderFlag = () => {
    if (flag) {
      let url = ""

      switch (flag) {
        case "AMERICAN EXPRESS":
          url = cardAmex
          break
        case "DINERSCLUB":
          url = cardDiners
          break
        case "DISCOVER":
          url = cardDiscover
          break
        case "ELO":
          url = cardElo
          break
        case "MASTERCARD":
          url = cardMaster
          break
        case "VISA":
          url = cardVisa
          break
        default:
          break
      }

      return (
        <img
          src={url}
          alt={""}
          style={{
            height: 32,
            width: "auto",
            alignSelf: "flex-start",
          }}
        />
      )
    }
  }

  const checkErrors = () => {
    try {
      let hasError = false

      const nameField =
        form.buyer.name.length < 1 ||
        form.buyer.name.trim().split(" ").length < 2

      // Validar se é celular válido (não fixo)
      const cleanedPhone = form.buyer.phone.replace(/\D/g, "")

      // Remove código do país se presente (55)
      let phoneWithoutCountry = cleanedPhone
      if (cleanedPhone.startsWith("55") && cleanedPhone.length === 13) {
        phoneWithoutCountry = cleanedPhone.slice(2)
      }

      const phoneValid = isPhoneNumberValid(form.buyer.phone, true)
      const isCellPhone =
        phoneWithoutCountry.length === 11 &&
        phoneWithoutCountry.length >= 3 &&
        phoneWithoutCountry[2] === "9"

      const phoneField = !phoneValid || !isCellPhone

      // Email: obrigatório no cartão; no PIX só valida formato se preenchido
      const emailField =
        method === "credit"
          ? form.buyer.email.length === 0 || !validEmail(form.buyer.email)
          : form.buyer.email.length > 0 && !validEmail(form.buyer.email)

      let ticketsIds: number[] = []

      const nominalsErrors =
        event?.nominal &&
        form.tickets.some((t, k) => {
          const status = t.person.name.trim().split(" ").length < 2

          if (status) ticketsIds.push(k)

          return status
        })

      let duplicateNames: number[] = []
      if (event?.nominal && form.tickets.length > 1) {
        const namesMap = new Map<string, number[]>()

        form.tickets.forEach((t, k) => {
          const name = t.person.name.trim().toLowerCase()
          if (name && name.split(" ").length >= 2) {
            if (!namesMap.has(name)) {
              namesMap.set(name, [])
            }
            namesMap.get(name)!.push(k)
          }
        })

        namesMap.forEach((indices) => {
          if (indices.length > 1) {
            duplicateNames.push(...indices)
          }
        })
      }

      if (nameField || emailField || phoneField) hasError = true

      if (nominalsErrors) hasError = true

      if (duplicateNames.length > 0) hasError = true

      return {
        has: hasError,
        fields: {
          name: nameField,
          phone: phoneField,
          email: emailField,
          ticketsIds: ticketsIds,
          duplicateNames: duplicateNames,
        },
      }
    } catch (error) {
      return {
        has: true,
        fields: {
          name: true,
          phone: true,
          email: true,
          ticketsIds: [],
          duplicateNames: [],
        },
      }
    }
  }

  const checkCardErrors = (): boolean => {
    if (form.card.number.replace(/\D/g, "").length < 13) return true

    const parts = form.card.date.split("/")
    if (parts.length !== 2 || parts[1].length < 2) return true
    const month = parseInt(parts[0], 10)
    const year2 = parseInt(parts[1], 10)
    if (isNaN(month) || isNaN(year2)) return true
    const now = new Date()
    const currYear = now.getFullYear() % 100
    const currMonth = now.getMonth() + 1
    if (month < 1 || month > 12) return true
    if (year2 < currYear) return true
    if (year2 === currYear && month < currMonth) return true

    if (form.card.code.replace(/\D/g, "").length < 3) return true
    if (form.card.cpf.replace(/\D/g, "").length < 11) return true
    if (form.card.name.trim().length < 1) return true

    if (installments === null) return true

    return false
  }

  const cartKey = getCartKey(tickets)
  const couponStale = !!coupon && cartKey !== "" && coupon.cartKey !== cartKey

  const showDenied = (message: string, ms = 3500) => {
    const f = { state: "denied", visible: true, message }
    setFeedback(f)
    setTimeout(() => setFeedback({ ...f, visible: false }), ms)
  }

  // Valida o cupom no backend para o carrinho. Cupom que zera o pedido não
  // dá para pagar por PIX/cartão: não é aplicado.
  const validateCoupon = async (
    code: string,
    list: TTicketDisposal[],
  ): Promise<
    | { ok: true; quote: TCouponQuote }
    | { ok: false; message: string; code?: string }
  > => {
    const res = await Api.post.coupon.validate({
      event_id: event?.id as string,
      code,
      products: getCouponProducts(list),
    })

    if (!res.ok)
      return {
        ok: false,
        message: getCouponErrorMessage(res.code, res.error),
        code: res.code,
      }

    if (res.data.total <= 0)
      return { ok: false, message: COUPON_ZERO_TOTAL_MESSAGE }

    return { ok: true, quote: { ...res.data, cartKey: getCartKey(list) } }
  }

  // Cupom deixou de valer: volta ao total sem desconto e avisa.
  const dropCoupon = (message: string, code?: string) => {
    setCoupon(null)
    setCouponError(message)
    showDenied(message, 4000)

    // Item do carrinho mudou no backend: recarrega os ingressos.
    if (code === "CART_ITEM_UNAVAILABLE") setTimeout(() => navigate("/"), 2500)
  }

  const handleApplyCoupon = async (code: string) => {
    setCouponLoading(true)
    setCouponError("")

    const res = await validateCoupon(code, tickets)

    if (res.ok) setCoupon(res.quote)
    else {
      setCouponError(res.message)
      if (res.code === "CART_ITEM_UNAVAILABLE")
        setTimeout(() => navigate("/"), 2500)
    }

    setCouponLoading(false)
  }

  const handleRemoveCoupon = () => {
    setCoupon(null)
    setCouponError("")
  }

  // Carrinho mudou depois de aplicar: revalida.
  useEffect(() => {
    if (!coupon || !couponStale) return

    let cancelled = false
    setCouponLoading(true)

    validateCoupon(coupon.code, tickets).then((res) => {
      if (cancelled) return
      if (res.ok) setCoupon(res.quote)
      else dropCoupon(res.message, res.code)
      setCouponLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [cartKey, couponStale])

  useEffect(() => {
    if (lctn.state?.couponError) {
      showDenied(lctn.state.couponError, 4000)

      // Lote trocado/inativo: recarrega os ingressos pela loja.
      if (lctn.state.couponErrorCode === "CART_ITEM_UNAVAILABLE")
        setTimeout(() => navigate("/"), 2500)
    }
  }, [])

  const getTaxes = () => {
    return sumTaxes({
      adminTax: (event as TEventData).eCommerce.adminTax,
      adminTaxMinimum: +(event as TEventData).eCommerce.adminTaxMinimum,
      adminTaxPercentage: +(event as TEventData).eCommerce.adminTaxPercentage,
      adminTaxValue: +(event as TEventData).eCommerce.adminTaxValue,
      tickets: tickets,
    })
  }

  const getTicketsList = (finalTaxValue: number) => {
    let ptickets: any[] = []

    if (event) {
      form.tickets.forEach((tt) => {
        const t = tickets.find((t) => t.id === tt.id) as TTicketDisposal

        const taxPerTicket = getTicketRelativeTaxes({
          ticketCost: t.price_sell,
          adminTax: event?.eCommerce?.adminTax,
          adminTaxMinimum: +event.eCommerce.adminTaxMinimum,
          adminTaxPercentage: +event.eCommerce.adminTaxPercentage,
          adminTaxValue: +event.eCommerce.adminTaxValue,
        })

        ptickets.push({
          price_sell: t.price_sell,
          batch_id: t.batch_id,
          customer_name: tt?.person.name,
          id: t.id,
          tax_value: taxPerTicket.value,
          ticketName: t.name,
          batchName: t.batch_name,
          isMesa: !!t.mesa_type,
          ticket_name: tt?.person.name,
          quantity: 1,
        })
      })
    }

    return ptickets
  }

  // Payment

  const goToPix = (activeCoupon: TCouponQuote | null) => {
    const taxes = getTaxes()

    let ptickets = getTicketsList(taxes.value)

    localStorage.removeItem("payed")
    localStorage.removeItem("paymentSession")

    navigate("/payment/pix", {
      state: {
        tickets: ptickets,
        disposalTickets: tickets,
        buyer: form.buyer,
        taxTotal: activeCoupon ? activeCoupon.taxTotal : taxes.value,
        coupon: activeCoupon ?? undefined,
        isNewOrder: true,
      },
    })
  }

  const goToCreditCard = async (activeCoupon: TCouponQuote | null) => {
    const taxes = getTaxes()

    const mpRes = await getMpInstance()
    if (!mpRes.ok) {
      const f = { state: "denied", visible: true, message: mpRes.error }
      setFeedback(f)
      setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
      return
    }
    const mp = mpRes.mp

    if (installments === null) {
      const f = {
        state: "denied",
        visible: true,
        message: "Selecione o parcelamento.",
      }
      setFeedback(f)
      setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
      return
    }

    const bin = form.card.number.replace(/\D/g, "").slice(0, 6)

    // Fluxo oficial (Core Methods): payment method e emissor vêm de
    // getPaymentMethods; getIssuers só quando additional_info_needed pede.
    let paymentMethodId: string | undefined
    let issuerId: string | null = installmentsInfo.issuerId
    try {
      const methodsRes = await mp.getPaymentMethods({ bin })
      const pm = methodsRes?.results?.[0]
      paymentMethodId = pm?.id ?? installmentsInfo.paymentMethodId ?? undefined

      if (pm?.additional_info_needed?.includes("issuer_id") && pm?.id) {
        const issuers = await mp.getIssuers({ paymentMethodId: pm.id, bin })
        const match = issuers?.find(
          (i: { id: string | number }) => String(i.id) === installmentsInfo.issuerId,
        )
        const chosen = match ?? issuers?.[0]
        if (chosen?.id != null) issuerId = String(chosen.id)
      } else if (pm?.issuer?.id != null) {
        issuerId = String(pm.issuer.id)
      }
    } catch {
      paymentMethodId = installmentsInfo.paymentMethodId ?? undefined
    }

    if (!paymentMethodId) {
      const f = {
        state: "denied",
        visible: true,
        message: "Não reconhecemos a bandeira do cartão. Confira o número.",
      }
      setFeedback(f)
      setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
      return
    }

    const [expMonth, expYear] = form.card.date.split("/")
    const tokenPayload = {
      cardNumber: form.card.number.replace(/\s/g, ""),
      cardholderName: form.card.name,
      cardExpirationMonth: expMonth,
      cardExpirationYear: "20" + expYear,
      securityCode: form.card.code,
      identificationType: "CPF",
      identificationNumber: form.card.cpf.replace(/\D/g, ""),
    }
    let cardToken
    try {
      cardToken = await mp.createCardToken(tokenPayload)
    } catch {
      const f = {
        state: "denied",
        visible: true,
        message: "Dados do cartão inválidos. Confira e tente novamente.",
      }
      setFeedback(f)
      setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
      return
    }

    const ptickets = getTicketsList(taxes.value)

    localStorage.removeItem("payed")
    localStorage.removeItem("paymentSession")

    navigate("/payment/card", {
      state: {
        tickets: ptickets,
        disposalTickets: tickets,
        buyer: form.buyer,
        taxTotal: activeCoupon ? activeCoupon.taxTotal : taxes.value,
        coupon: activeCoupon ?? undefined,
        isNewOrder: true,
        cardToken: cardToken.id,
        paymentMethodId,
        installments,
        issuerId,
        payer: {
          email: form.buyer.email,
          first_name: form.card.name.trim().split(" ")[0],
          last_name:
            form.card.name.trim().split(" ").slice(1).join(" ") ||
            form.card.name.trim().split(" ")[0],
          identification: { type: "CPF", number: form.card.cpf.replace(/\D/g, "") },
        },
      },
    })
  }

  const handlePay = async () => {
    const errors = checkErrors()

    if (!errors.has && event) {
      setIsValidating(true)

      let eInfo, productsRes
      try {
        eInfo = await Api.get.eventInfo({ eventId: event.id as string })
        if (!eInfo.ok || !eInfo.data?.status) {
          setIsValidating(false)
          const f = {
            state: "denied",
            message: "O evento não está mais ativo.",
            visible: true,
          }
          setFeedback(f)
          setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
          return
        }

        productsRes = await Api.get.products({ eventId: event.id as string })
        if (!productsRes.ok) {
          setIsValidating(false)
          const f = {
            state: "denied",
            message: "Erro ao validar estoques dos ingressos.",
            visible: true,
          }
          setFeedback(f)
          setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
          return
        }
      } catch (error) {
        setIsValidating(false)
        const f = {
          state: "denied",
          message: "Houve um erro ao validar sua compra. Tente novamente.",
          visible: true,
        }
        setFeedback(f)
        setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
        return
      }

      const availableProducts = productsRes.data.list
      for (const t of tickets.filter((t) => t.qnt > 0)) {
        const product = availableProducts.find((p: any) => p.id === t.id)
        if (!product || product.quantity < t.qnt) {
          setIsValidating(false)
          const f = {
            state: "denied",
            message: `Estoque insuficiente para o ingresso ${t.name}.`,
            visible: true,
          }
          setFeedback(f)
          setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
          return
        }
      }

      // Sem login de conta (ou visitante de uma compra anterior): garante o
      // cadastro do telefone, sem código e sem sessão. Quem entrou com o
      // código segue como está. O botão segue em "Validando..." até a
      // resposta, para um segundo toque não disparar outro checkout.
      if (needsGuestCheckout(user)) {
        const guest = await startGuestCheckout(form.buyer)

        if (!guest.ok) {
          setIsValidating(false)
          const f = { state: "denied", visible: true, message: guest.error }
          setFeedback(f)
          setTimeout(() => setFeedback({ ...f, visible: false }), 3500)
          return
        }
      }

      // O cupom pode ter esgotado/expirado desde que foi aplicado.
      let activeCoupon: TCouponQuote | null = null
      if (coupon) {
        const res = await validateCoupon(coupon.code, tickets)

        if (!res.ok) {
          setIsValidating(false)
          dropCoupon(res.message, res.code)
          return
        }

        activeCoupon = res.quote
        setCoupon(res.quote)
      }

      setIsValidating(false)

      if (method === "pix") goToPix(activeCoupon)
      else if (method === "credit") await goToCreditCard(activeCoupon)
    } else {
      let fieldsErrors = []
      let fieldsStr = ""

      let newErrorValue = { ...formErrors }

      setTouched({
        buyerName: true,
        buyerPhone: true,
        buyerEmail: true,
        cardCpf: true,
        cardCvv: true,
      })

      if (errors.fields.name) {
        fieldsErrors.push("Nome")
        newErrorValue.buyerName = true
      }
      if (errors.fields.phone) {
        fieldsErrors.push("Celular válido")
        newErrorValue.buyerPhone = true
      }
      if (errors.fields.email) {
        fieldsErrors.push("E-mail")
        newErrorValue.buyerEmail = true
      }
      // Inicializa o array de ticketsIds com erros de validação de nome/sobrenome
      if (errors.fields.ticketsIds.length > 0) {
        fieldsErrors.push("Nome e sobrenome")
        newErrorValue.ticketsIds = errors.fields.ticketsIds
      } else {
        newErrorValue.ticketsIds = []
      }

      // Adiciona erros de nomes duplicados, mesclando e removendo duplicatas
      if (
        errors.fields.duplicateNames &&
        errors.fields.duplicateNames.length > 0
      ) {
        fieldsErrors.push("Nomes duplicados nos participantes")
        newErrorValue.ticketsIds = Array.from(
          new Set([
            ...newErrorValue.ticketsIds,
            ...errors.fields.duplicateNames,
          ]),
        )
      }

      setFormErrors(newErrorValue)

      // Formata os erros com bullets para melhor visualização
      const formattedErrors = fieldsErrors
        .map((error) => `• ${error}`)
        .join("\n")
      fieldsStr = formattedErrors

      const f = {
        state: "expired",
        visible: true,
        message: `Preencha os campos corretamente:\n${fieldsStr}`,
      }
      setFeedback(f)

      setTimeout(() => {
        setFeedback({ ...f, visible: false })

        setTimeout(() => {
          setCanBuy(true)
        }, 400)
      }, 3500)
    }
  }

  const handleConfirmPhone = (phone: string) => {
    handleForm("phone", phone)
    setShowingPhoneModal(false)

    setPayAfterPhoneConfirm(true)
  }

  // Paga no render seguinte, com o telefone confirmado já no form.
  useEffect(() => {
    if (payAfterPhoneConfirm) {
      setPayAfterPhoneConfirm(false)
      handlePay()
    }
  }, [payAfterPhoneConfirm])

  const clearFieldError = (field: string, ticketId?: number) => {
    if (
      field !== "ticketsIds" &&
      formErrors[field as keyof typeof formErrors]
    ) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: false,
      }))
    } else if (
      field === "ticketsIds" &&
      ticketId !== undefined &&
      ticketId > -1
    ) {
      setFormErrors((prev) => ({
        ...prev,
        ticketsIds: prev.ticketsIds.filter((i) => i !== ticketId),
      }))
    }
  }

  const loadEventData = useCallback(async () => {
    if (event) {
      try {
        const req = await Api.get.eventInfo({ eventId: event?.id })

        if (req.ok) {
          const data = req.data
          sessionStorage.setItem("event", JSON.stringify(data))
          controllers.event.setData(data)
        }
      } catch (error) {
        alert("Erro ao carregar os tickets")
      }
    } else navigate("/eventSelect")
  }, [])

  useEffect(() => {
    let ticketsList: any[] = []

    tickets.forEach((t) => {
      for (let k = 0; k <= t.qnt - 1; k++) {
        ticketsList.push({ ...t, oid: k, person: { name: "" } })
      }
    })

    setForm({
      ...form,
      tickets: ticketsList,
    })

    // eslint-disable-next-line
  }, [tickets])

  useEffect(() => {
    // Token expirado que sobra de uma sessão anterior (comum ao alternar entre
    // eventos de um mesmo organizador) faz o interceptor do axios forçar um
    // window.location.reload() no meio das checagens de handlePay, travando o
    // botão em "Validando..." sem nenhuma mensagem. Limpa proativamente aqui,
    // do mesmo jeito que a Home já faz.
    if (user) {
      try {
        const token = localStorage.getItem("token")

        if (token && checkTokenExpiration(token)) {
          localStorage.removeItem("token")
          controllers.user.clear()
          sessionStorage.removeItem("user")
        }
      } catch (error) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!user) setCanBuy(false)

    if (lctn.state) {
      // When returning from CardError, disposalTickets holds the original TTicketDisposal[]
      const pickedTickets =
        lctn.state.disposalTickets ?? lctn.state.tickets ?? null

      if (pickedTickets) {
        setTickets(pickedTickets)
      } else navigate("/")
    } else navigate("/")
  }, [lctn.state, navigate])

  useEffect(() => {
    if (event?.nominal) {
      const ticketsUsersOk = form.tickets.every((t) => !!t.person.name.trim())
      const buyerOk = !checkErrors().has && ticketsUsersOk
      setFieldsOk(method === "credit" ? buyerOk && !checkCardErrors() : buyerOk)
    } else {
      const buyerOk = !checkErrors().has
      setFieldsOk(method === "credit" ? buyerOk && !checkCardErrors() : buyerOk)
    }
  }, [form.buyer, form.tickets, form.card, method, installments])

  useEffect(() => {
    loadEventData()
  }, [loadEventData])

  useEffect(() => {
    if (!event) return
    const pix = event.eCommerce?.pix !== false
    const credit = event.eCommerce?.credit !== false
    if (method === "pix" && !pix) setMethod(credit ? "credit" : "")
    if (method === "credit" && !credit) setMethod(pix ? "pix" : "")
  }, [event?.eCommerce?.pix, event?.eCommerce?.credit])

  const cardBin = form.card.number.replace(/\D/g, "").slice(0, 6)

  // Valor total do pedido em reais — o mesmo que PaymentCard envia ao backend.
  let orderAmount = 0
  if (method === "credit" && event && tickets.length > 0) {
    try {
      if (coupon) {
        orderAmount =
          getCouponCharge(coupon, {
            method: "credit",
            additionalTaxCard: event.eCommerce?.additionalTaxCard,
          }) / 100
      } else {
        const taxes = getTaxes()
        const baseAmount = pageTools.order.getOrderValue(
          getTicketsList(taxes.value),
          form.buyer,
          taxes.value,
          "",
          user as TUser,
          event.dk as string,
          event.id as string,
          event.name as string,
          event.eCommerce?.chargeClient ?? false,
        )
        orderAmount =
          (baseAmount +
            getAdditionalTax({
              baseTotal:
                baseAmount -
                (event.eCommerce?.chargeClient ? taxes.value : 0),
              method: "credit",
              additionalTaxCard: event.eCommerce?.additionalTaxCard,
            })) /
          100
      }
    } catch {
      orderAmount = 0
    }
  }

  const installmentsInfo = useCardInstallments({
    enabled: method === "credit",
    bin: cardBin.length === 6 ? cardBin : "",
    amount: orderAmount,
  })

  const allowInstallments = event?.eCommerce?.allowInstallments === true

  useEffect(() => {
    if (!allowInstallments) {
      setInstallments(1)
      return
    }
    if (installmentsInfo.loading) return
    setInstallments((prev) =>
      prev !== null &&
      installmentsInfo.options.some((o) => o.installments === prev)
        ? prev
        : null,
    )
  }, [installmentsInfo.options, installmentsInfo.loading, allowInstallments])

  const currentErrors = checkErrors().fields
  const buyerNameError =
    touched.buyerName && currentErrors.name
      ? "Informe nome e sobrenome"
      : undefined
  const buyerPhoneError =
    touched.buyerPhone && currentErrors.phone
      ? "Informe um celular válido com DDD"
      : undefined
  const buyerEmailError =
    touched.buyerEmail && currentErrors.email
      ? method === "credit" && form.buyer.email.length === 0
        ? "Informe um e-mail para receber o comprovante"
        : "Informe um e-mail válido"
      : undefined
  const cardCpfError =
    touched.cardCpf && form.card.cpf.length > 0 && !isValidCpf(form.card.cpf)
      ? "CPF inválido"
      : undefined
  const cardCvvError =
    touched.cardCvv && form.card.code.length > 0 && !isValidCVV(form.card.code)
      ? "CVV inválido"
      : undefined
  const emailHelper =
    method === "credit"
      ? "Enviaremos o comprovante por e-mail"
      : "Opcional — enviamos os ingressos por e-mail"

  return (
    <S.Page>
      <ConfirmPhoneNumberModal
        shown={showingPhoneModal}
        basePhone={form.buyer.phone}
        handleConfirm={handleConfirmPhone}
        handleClose={() => setShowingPhoneModal(false)}
      />

      <Feedback data={feedback} />

      <Header />

      <Container>
        <S.Main>
          <S.EventResume>
            <S.PaymentData>
              <S.PaymentTitle>Como você quer pagar?</S.PaymentTitle>
              <S.MethodSelector role="radiogroup" aria-label="Forma de pagamento">
                {pixEnabled && (
                  <S.MethodCard
                    type="button"
                    role="radio"
                    aria-checked={method === "pix"}
                    $active={method === "pix"}
                    onClick={() => setMethod("pix")}
                  >
                    <S.MethodPixImage
                      src={pixImage}
                      alt="Pix"
                      $active={method === "pix"}
                    />
                    <S.PixLabelMobileOnly $active={method === "pix"}>
                      Pix
                    </S.PixLabelMobileOnly>
                    <S.MethodCheck $active={method === "pix"}>
                      <Check size={10} strokeWidth={3} />
                    </S.MethodCheck>
                  </S.MethodCard>
                )}

                {creditEnabled && (
                  <S.MethodCard
                    type="button"
                    role="radio"
                    aria-checked={method === "credit"}
                    $active={method === "credit"}
                    onClick={() => setMethod("credit")}
                  >
                    <S.MethodIconWrap $active={method === "credit"}>
                      <CreditCard size={26} strokeWidth={1.75} />
                    </S.MethodIconWrap>
                    <S.MethodInfo>
                      <S.MethodName $active={method === "credit"}>
                        Cartão de crédito
                      </S.MethodName>
                    </S.MethodInfo>
                    <S.MethodCheck $active={method === "credit"}>
                      <Check size={10} strokeWidth={3} />
                    </S.MethodCheck>
                  </S.MethodCard>
                )}
              </S.MethodSelector>
            </S.PaymentData>

            <S.Form>
              <S.FormBlock $k={1}>
                <span>Informações do(a) comprador(a)</span>

                <S.FormLines>
                  <S.FormLine>
                    <Input
                      label={"Nome e sobrenome"}
                      value={form.buyer.name}
                      onChange={(v: string) => {
                        handleForm("name", v)
                        clearFieldError("buyerName")
                      }}
                      onBlur={() => markTouched("buyerName")}
                      error={buyerNameError ?? formErrors.buyerName}
                      noNumbers={true}
                      capitalizeWords={true}
                    />
                  </S.FormLine>
                  <S.FormLine>
                    <Input
                      label={"Telefone"}
                      value={form.buyer.phone}
                      onChange={(v: string) => {
                        handleForm("phone", v)
                        clearFieldError("buyerPhone")
                      }}
                      onBlur={() => markTouched("buyerPhone")}
                      inputMode="numeric"
                      helper={"Seus ingressos ficam vinculados a este número"}
                      error={buyerPhoneError ?? formErrors.buyerPhone}
                    />
                    <Input
                      label={"Email"}
                      value={form.buyer.email}
                      onChange={(v: string) => {
                        handleForm("email", v)
                        clearFieldError("buyerEmail")
                      }}
                      onBlur={() => markTouched("buyerEmail")}
                      type="email"
                      enterKeyHint={"done"}
                      helper={emailHelper}
                      error={buyerEmailError ?? formErrors.buyerEmail}
                    />
                  </S.FormLine>
                </S.FormLines>
              </S.FormBlock>

              {Boolean(event?.nominal) && (
                <S.FormBlock $k={2}>
                  <span>Informações dos participantes</span>

                  {form.tickets.map((ticket, k) => (
                    <S.TicketBlock key={k}>
                      <S.TicketName>
                        <span>Ingresso {k + 1}: </span>
                        <span>{ticket.name}</span>
                      </S.TicketName>

                      {k === 0 && (
                        <S.Checkbox htmlFor={`check-${k}`}>
                          <input
                            id={`check-${k}`}
                            type="checkbox"
                            checked={
                              !!ticket.person.name &&
                              ticket.person.name === form.buyer.name
                            }
                            onClick={() => {
                              // eslint-disable-next-line react-hooks/rules-of-hooks
                              usePayer(
                                ticket,
                                !(
                                  !!ticket.person.name &&
                                  ticket.person.name === form.buyer.name
                                ),
                              )
                            }}
                          />
                          <span>Utilizar dados do(a) comprador(a)</span>
                        </S.Checkbox>
                      )}

                      <S.FormLines>
                        <S.FormLine>
                          <Input
                            label={"Nome e sobrenome"}
                            value={ticket.person.name}
                            onChange={(v: string) => {
                              handleTicketForm(ticket, "name", v)
                              clearFieldError("ticketsIds", k)
                            }}
                            error={formErrors.ticketsIds.includes(k)}
                          />
                        </S.FormLine>
                      </S.FormLines>
                    </S.TicketBlock>
                  ))}
                </S.FormBlock>
              )}

              <S.FormBlock $k={3}>
                <CouponField
                  appliedCode={coupon?.code ?? null}
                  discountTotal={coupon?.discountTotal ?? 0}
                  loading={couponLoading}
                  error={couponError}
                  disabled={isValidating || tickets.every((t) => t.qnt <= 0)}
                  onApply={handleApplyCoupon}
                  onRemove={handleRemoveCoupon}
                  onClearError={() => setCouponError("")}
                />
              </S.FormBlock>

              <S.CardFormCollapse
                $open={method === "credit"}
                aria-hidden={method !== "credit"}
              >
                <div>
                <S.CardFormBlock>
                  <S.CardFormHeader>
                    <Lock size={16} strokeWidth={2.25} />
                    Dados do cartão
                  </S.CardFormHeader>

                  <S.FormLines>
                    <S.CardNumberRow>
                      <Input
                        label={"Número do cartão"}
                        value={form.card.number}
                        onChange={(v: string) => handleCard("number", v)}
                        inputMode="numeric"
                      />
                      {renderFlag()}
                    </S.CardNumberRow>

                    <S.FormLine>
                      <Input
                        label={"Nome impresso no cartão"}
                        value={form.card.name}
                        onChange={(v: string) => handleCard("name", v)}
                        noNumbers={true}
                      />
                    </S.FormLine>

                    <S.FormLine>
                      <Input
                        label={"Validade (MM/AA)"}
                        value={form.card.date}
                        onChange={(v: string) => handleCard("date", v)}
                        inputMode="numeric"
                      />
                      <Input
                        label={"CVV"}
                        value={form.card.code}
                        onChange={(v: string) => handleCard("code", v)}
                        inputMode="numeric"
                        error={cardCvvError}
                      />
                    </S.FormLine>

                    <S.FormLine>
                      <Input
                        label={"CPF do titular"}
                        value={form.card.cpf}
                        onChange={(v: string) => handleCard("cpf", v)}
                        inputMode="numeric"
                        error={cardCpfError}
                      />
                    </S.FormLine>

                  </S.FormLines>

                  {allowInstallments && (
                    <InstallmentSelect
                      options={installmentsInfo.options}
                      selected={installments}
                      loading={installmentsInfo.loading}
                      error={installmentsInfo.error}
                      hasCardData={cardBin.length === 6}
                      onSelect={setInstallments}
                    />
                  )}
                </S.CardFormBlock>
                </div>
              </S.CardFormCollapse>

              <S.Button
                $disabled={!fieldsOk || isValidating || couponLoading || couponStale}
                onClick={
                  !isValidating && !couponLoading && !couponStale
                    ? () => {
                        if (method === "credit") {
                          markTouched("cardCpf")
                          markTouched("cardCvv")
                        }
                        const cardCpfInvalid =
                          method === "credit" && !isValidCpf(form.card.cpf)
                        const cardCvvInvalid =
                          method === "credit" && !isValidCVV(form.card.code)
                        if (fieldsOk && !cardCpfInvalid && !cardCvvInvalid)
                          setShowingPhoneModal(true)
                      }
                    : undefined
                }
              >
                <Lock size={18} strokeWidth={2.25} />
                {isValidating ? "Validando..." : "Pagar com segurança"}
              </S.Button>
            </S.Form>
          </S.EventResume>

          <OrderResume
            datePeriod={getDatePeriod(
              event?.date_ini as string,
              event?.date_end as string,
            )}
            ticketsList={tickets.filter((t) => t.qnt > 0)}
            setTickets={setTickets}
            paymentMethod={method}
            coupon={coupon && !couponStale ? coupon : null}
          />
        </S.Main>
      </Container>

      <Footer />
    </S.Page>
  )
}

export default Payment
